import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { cloneDeep, setObjectValue, toName, unsub } from '@util/utils';
import { DataService } from '@data/data.service';
import {
  editColumn,
  getRowStyle,
  OrdersGridComponent,
} from '../../grid/orders-grid/orders-grid.component';
import { ToolbarService } from '../../toolbar/toolbar.service';
import { findOrderStatusNameById } from '../order-basic';
import { NavigationService } from '@services/navigation.service';
import {
  CONFIG_KEYS,
  isAdminType,
  StateService,
} from '@services/state.service';
import { GridColumnStyles } from '@common/common';
import {
  statusColumn,
  teamColumn,
  orderDateColumn,
  customerColumn,
  licensePlateColumn,
  kindColumn,
  orderNrColumnForMontage,
  infoColumn,
} from '../order-columns';
import { Subscription } from 'rxjs';
import {
  OrderChangeEvent,
  OrderDialogService,
  ORDER_OPERATIONS,
} from '../order-dialog.service';
import { BackendPaths } from '@common/backend-paths';
import { toRequestDate } from '@util/date-utils';
import { GlobalEventServiceService, GlobalEventType } from '@services/global-event-service.service';
import { OrderEventType } from '../order-events';
import { orderGridOptionTypes } from './order-grid-option-types';
import { EditCellRendererEvent, EditCellRendererEventType } from '@grid-cell/edit-cell-renderer.component';

const mapping: any = {
  assigned: 'Zugewiesene Aufträge',
  cancelled: 'Stornierte Aufträge',
  complete: 'Ausgeführte Aufträge',
  open: 'Offene Aufträge',
  billed: 'Abgerechnete Aufträge',
};

@Component({
  selector: 'app-open-orders',
  templateUrl: './open-orders.component.html',
})
export class OpenOrdersComponent implements OnInit, OnDestroy {
  public _grid!: OrdersGridComponent;

  startDate?: Date;

  endDate?: Date;

  allOrders = false;

  lastOptions: any;

  rows: any[] = [];

  lastFilterValue = '';
  filterValues: any;

  orderGridType = '';

  gridOptionsForMontage: any = {
    headerHeight: 40,
    defaultColDef: {
      headerClass: 'def-col',
      autoHeight: true,
      cellClass: GridColumnStyles.CELL_CLASS_STRING,
    },
    columnDefs: [
      orderNrColumnForMontage,
      statusColumn,
      infoColumn,
      teamColumn,
      orderDateColumn,
      customerColumn,
      licensePlateColumn,
      kindColumn,
      editColumn,
    ],
    enableCellTextSelection: true,
    alwaysShowHorizontalScroll: true,
    alwaysShowVerticalScroll: true,
    getRowStyle,
  };

  isAdmin: isAdminType = true;

  eventSub?: Subscription;
  stateSub?: Subscription;

  orderChangeSub?: Subscription;
  configChangeSub?: Subscription;

  accessUrl = '';

  onFilterChange($event: any) {
    return;
  }

  onFilterClickChange($event: any) {
    this.filterValues = $event;
    this.reloadData();
  }

  setGrid(grid: OrdersGridComponent) {
    this._grid = grid;
    this.updateGridOptions();
    this.updateGrid();
  }

  updateGridOptions() {
    if (this._grid) {
      let options: any = OrdersGridComponent.defaultOptions();

      if (this.isAdmin) {
        if (this.orderGridType && orderGridOptionTypes[this.orderGridType]) {
          options = orderGridOptionTypes[this.orderGridType];
        }
      } else {
        options = this.gridOptionsForMontage;
      }

      if (options) {
        const sl = options.columnDefs.length;
        const last = options.columnDefs[sl - 1];
        if (last.cellRendererParams?.onClick) {
          last.cellRendererParams = null;
        }
        const lastCopy = cloneDeep(last);
        lastCopy.cellRendererParams.onClick = (e: any) => {
          this.onEditClick(e);
        };

        options.columnDefs[sl - 1] = lastCopy;

        this.lastOptions = options;

        this._grid.setGridOptions(options);
      }
    }
  }

  updateGrid() {
    this._grid && this._grid.setRowData(this.rows);
  }

  @ViewChild('grid', {
    read: OrdersGridComponent,
  })
  set grid(grid: OrdersGridComponent) {
    this.setGrid(grid);
  }

  constructor(
    private currentRoute: ActivatedRoute,
    private dataService: DataService,
    private navService: NavigationService,
    private toolbarService: ToolbarService,
    private stateService: StateService,
    private orderDialogService: OrderDialogService,
    private eventService: GlobalEventServiceService,
    private router: Router
  ) { }

  ngOnInit(): void {
    let title = 'Alle Aufträge';

    const urlParts: UrlSegment[] = this.currentRoute.snapshot.url;

    let status = (urlParts.length > 1) ? urlParts[1].path : '';

    this.orderGridType = status;

    if (status && mapping[status]) {
      title = mapping[status];
    } else {
      this.allOrders = true;
    }

    if (status) {
      //status = '/' + status;
    }

    this.toolbarService.setTitle(title);

    this.isAdmin = this.stateService.isAdmin();

    this.eventSub = this.eventService.listen().subscribe((evt: GlobalEventType) => {
      if (evt && OrderEventType.RECEIPTS_GENERATED === evt.type) {
        //MyLog.log('Reload by receipts');
        this.reloadData();
      }
    });

    this.stateSub = this.stateService
      .adminChange()
      .subscribe((isAdmin: isAdminType) => {
        this.isAdmin = isAdmin;
        this.updateGridOptions();
      });

    this.orderChangeSub = this.orderDialogService
      .orderChanges()
      .subscribe((change: OrderChangeEvent) => {
        if (change.type === ORDER_OPERATIONS.DELETED) {
          this.reloadData();
        }
      });

    this.accessUrl = BackendPaths.ORDERS_FULL + status;

    this.setDates();

    this.configChangeSub = this.stateService
      .configChange()
      .subscribe((e: any) => {
        this.setDates();
      });

    this.filterValues = {
      text: '',
      from: this.startDate,
      to: this.endDate,
    };

    this.loadData(this.accessUrl, false);
  }

  ngOnDestroy(): void {
    const options = this.lastOptions;

    if (options) {
      const sl = options.columnDefs.length;
      const last = options.columnDefs[sl - 1];
      if (last.cellRendererParams?.onClick) {
        last.cellRendererParams = null;
      }
    }
    this.eventSub = unsub(this.eventSub);
    this.stateSub = unsub(this.stateSub);
    this.orderChangeSub = unsub(this.orderChangeSub);
    this.configChangeSub = unsub(this.configChangeSub);
  }

  loadData(url: string, checkFilters: boolean) {
    const params: any = {};

    const f = this.filterValues;

    if (f) {
      f.text && setObjectValue(params, 'text', f.text);
      f.from && setObjectValue(params, 'from', toRequestDate(f.from));
      f.to && setObjectValue(params, 'to', toRequestDate(f.to));
    }

    const isAdmin = this.stateService.isAdmin();

    this.dataService.get(url, params).subscribe((result: any) => {
      const rows = result?.result?.rows;

      if (Array.isArray(rows)) {
        rows.forEach((item: any) => {
          if (!item.status_name) {
            item.status_name = findOrderStatusNameById(item.status);
          }
          if (item.team && !item.team_name) {
            item.team_name = toName(item.team);
          }
          if (!item.order_nr) {
            item.order_nr = item.id;
          }
          if (item.customer && !item.customer_name) {
            item.customer_name = toName(item.customer);
          }
          if (item.partner && !item.partner_name) {
            item.partner_name = toName(item.partner);
          }

          if (isAdmin && item.hasOwnProperty('deductible')) {
            item.nd = item.deductible < 0.000001;
          }
        });

        this.rows = rows;

        this.updateGrid();
      }
    });
  }

  onEditClick(evt: EditCellRendererEvent) {
    if (!evt) {
      return;
    }
    const data = evt.data;

    switch (evt.type) {
      case EditCellRendererEventType.VIEW:
        let url: any;
        if (data.url) {
          url = data.url;
        } else if (data.id) {
          url = '/orders/' + data.id;
        }

        url && this.router.navigate([url]);
        break;
        case EditCellRendererEventType.DELETE:
          if(data && data.id) {
            this.orderDialogService.tryDeleteOrder(data.id, data, false);
          }
          break;
      default:
        console.error('Unknown event:', evt);
    }
  }

  setDates() {
    const startDate = this.stateService.getConfigurationFor(
      CONFIG_KEYS.ORDER_START_DATE
    );

    const endDate = this.stateService.getConfigurationFor(
      CONFIG_KEYS.ORDER_END_DATE,
      startDate
    );

    this.startDate = startDate;

    this.endDate = endDate;
  }

  reloadData() {
    this.loadData(this.accessUrl, true);
  }

  addNewOrder() {
    return this.navService.goToNewOrder();
  }
}
